import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, X, Image, FileText, Film, Mic, Square, Trash2, Smile, Zap, Plus, Edit2, LayoutTemplate, MapPin } from 'lucide-react';
import { useQuickReplies } from '../../hooks/useQuickReplies';
import { useTenant } from '../../hooks/useTenant';
import QuickReplyManager from '../QuickReplies/QuickReplyManager';
import EmojiPicker from 'emoji-picker-react';
import WaTemplateSelectorModal from './WaTemplateSelectorModal';

/**
 * Message input component with file attachment and voice recording
 */
const MessageInput = ({ onSend, onSendFile, disabled, isMobile, replyToMessage, onCancelReply, editingMessage, onCancelEdit, draftMessage, onDraftConsumed, currentPhone, isOfficialTenant }) => {
    const { currentTenant } = useTenant();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [message, setMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const textareaRef = useRef(null);

    // Close emoji picker on click outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (showEmojiPicker &&
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(event.target) &&
                emojiButtonRef.current &&
                !emojiButtonRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showEmojiPicker]);

    // Quick Replies State
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const [quickReplyFilter, setQuickReplyFilter] = useState('');
    const [showQuickReplyManager, setShowQuickReplyManager] = useState(false);
    const [editingQuickReply, setEditingQuickReply] = useState(null);

    // Load quick replies
    const { quickReplies, fetchQuickReplies, deleteQuickReply } = useQuickReplies();

    // Check for quick reply trigger
    useEffect(() => {
        const lastSlashIndex = message.lastIndexOf('/');
        if (lastSlashIndex !== -1) {
            const textAfterSlash = message.substring(lastSlashIndex + 1);
            // Only trigger if no spaces after last slash (part of the shortcut)
            if (!textAfterSlash.includes(' ')) {
                setQuickReplyFilter(textAfterSlash.toLowerCase());
                setShowQuickReplies(true);
                return;
            }
        }
        setShowQuickReplies(false);
    }, [message]);

    const handleQuickReplySelect = async (reply) => {
        const lastSlashIndex = message.lastIndexOf('/');
        const textBeforeSlash = message.substring(0, lastSlashIndex);

        setMessage(textBeforeSlash + reply.content + ' ');
        setShowQuickReplies(false);

        // Handle Media Attachment
        if (reply.media_url) {
            setIsUploading(true);
            try {
                let url = reply.media_url;

                // Fix: If on mobile/remote device and URL is localhost, replace with current hostname
                if (url.includes('localhost') && window.location.hostname !== 'localhost') {
                    url = url.replace('localhost', window.location.hostname);
                }

                console.log('Fetching media from:', url);
                const response = await fetch(url);

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const blob = await response.blob();

                // Determine file name and type
                const filename = url.split('/').pop() || 'media_attachment';
                const file = new File([blob], filename, { type: blob.type });

                setSelectedFiles([file]);

                // Generate Preview if Image
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (e) => setFilePreviews([e.target.result]);
                    reader.readAsDataURL(file);
                } else {
                    setFilePreviews([null]);
                }

            } catch (error) {
                console.error('Error fetching quick reply media:', error);
                alert(`Error al cargar la imagen: ${error.message}`);
                setSelectedFiles([]);
                setFilePreviews([]);
            } finally {
                setIsUploading(false);
                // Focus AFTER isUploading=false so the textarea is no longer disabled
                setTimeout(() => textareaRef.current?.focus(), 50);
            }
        } else {
            // No media — focus immediately
            setTimeout(() => textareaRef.current?.focus(), 0);
        }
    };

    const filteredReplies = quickReplies.filter(qr =>
        qr.shortcut.toLowerCase().startsWith(quickReplyFilter) ||
        qr.content.toLowerCase().startsWith(quickReplyFilter)
    );

    // Voice recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioWaveform, setAudioWaveform] = useState([]);
    const handleEmojiClick = (emojiData) => {
        const emoji = emojiData.emoji;
        const textarea = document.querySelector('.message-input');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = message.substring(0, start) + emoji + message.substring(end);

        setMessage(newText);

        // Return focus to textarea and set cursor position correctly
        setTimeout(() => {
            textarea.focus();
            const newPos = start + emoji.length;
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    // Handle editing message
    useEffect(() => {
        if (editingMessage && editingMessage.text) {
            setMessage(editingMessage.text);
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const length = editingMessage.text.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [editingMessage]);

    // Handle draft message injection
    useEffect(() => {
        if (draftMessage) {
            setMessage(draftMessage);
            if (onDraftConsumed) onDraftConsumed();
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const length = draftMessage.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [draftMessage, onDraftConsumed]);

    // Auto-resize textarea when message changes (for quick replies or external updates)
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 250) + 'px';
        }
    }, [message]);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const analyserRef = useRef(null);
    const animationRef = useRef(null);

    // Format recording time
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Start voice recording
    const startRecording = async () => {
        try {
            // Check for browser support
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert('Tu navegador no soporta grabación de audio. Asegúrate de usar HTTPS.');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Detect supported MIME types for maximum compatibility (iOS/Safari vs Android/Chrome)
            const mimeTypes = [
                'audio/webm;codecs=opus',
                'audio/mp4',
                'audio/ogg;codecs=opus',
                'audio/webm',
                'audio/aac'
            ];

            let supportedMimeType = '';
            if (window.MediaRecorder) {
                for (const type of mimeTypes) {
                    if (MediaRecorder.isTypeSupported(type)) {
                        supportedMimeType = type;
                        break;
                    }
                }
            } else {
                console.warn('⚠️ MediaRecorder is not supported in this browser');
            }

            console.log(`🎙️ Using supported MIME type: ${supportedMimeType || 'browser default'}`);

            // Create audio context for visualization (User gesture requirement)
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContextClass();

            // Resume context if suspended (common in PWAs/Safari)
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Start visualization
            const updateWaveform = () => {
                if (!analyserRef.current) return;
                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteFrequencyData(dataArray);

                // Get 20 bars from the frequency data
                const bars = [];
                const step = Math.floor(dataArray.length / 20);
                for (let i = 0; i < 20; i++) {
                    const value = dataArray[i * step] / 255;
                    bars.push(value);
                }
                setAudioWaveform(bars);
                animationRef.current = requestAnimationFrame(updateWaveform);
            };
            updateWaveform();

            // Create media recorder with supported settings
            const options = supportedMimeType ? { mimeType: supportedMimeType } : {};
            const mediaRecorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                // Use the same MIME type used for recording
                const finalType = supportedMimeType || mediaRecorder.mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: finalType });

                console.log(`🔊 Recording stopped. Size: ${audioBlob.size} bytes, Type: ${finalType}`);

                setAudioBlob(audioBlob);
                setAudioUrl(URL.createObjectURL(audioBlob));
                stream.getTracks().forEach(track => track.stop());
                audioContext.close();
            };

            mediaRecorder.start(1000); // Collect data every second for safety
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (error) {
            console.error('❌ Error accessing microphone:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                alert('Permiso denegado. Habilita el micrófono en la configuración de tu navegador.');
            } else if (error.name === 'NotFoundError') {
                alert('No se encontró ningún micrófono conectado.');
            } else {
                alert(`Error al iniciar grabación: ${error.message}`);
            }
        }
    };

    // Stop recording
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            setAudioWaveform([]);
        }
    };

    // Cancel recording
    const cancelRecording = () => {
        stopRecording();
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
    };

    // Send audio
    const sendAudio = async () => {
        if (!audioBlob || !onSendFile) return;

        setIsUploading(true);
        try {
            // Determine extension based on actual blob type
            const extension = audioBlob.type.includes('mp4') ? 'mp4' :
                audioBlob.type.includes('ogg') ? 'ogg' : 'webm';

            // Create a file from the blob with the correct type
            const audioFile = new File([audioBlob], `audio_${Date.now()}.${extension}`, { type: audioBlob.type });
            await onSendFile(audioFile, '');
            cancelRecording();
        } catch (error) {
            console.error('Error sending audio:', error);
            alert('Error al enviar el audio');
        } finally {
            setIsUploading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (audioBlob) {
            sendAudio();
        } else if (selectedFiles.length > 0) {
            handleSendFile();
        } else if (message.trim() && !disabled) {
            onSend(message.trim());
            setMessage('');
            setShowEmojiPicker(false);
        }
    };


    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            if (files.some(f => f.size > 100 * 1024 * 1024)) {
                alert('Uno o más archivos superan el límite máximo de 100MB permitido.');
                return;
            }
            setSelectedFiles(prev => [...prev, ...files]);
            
            files.forEach(file => {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (e) => setFilePreviews(prev => [...prev, e.target.result]);
                    reader.readAsDataURL(file);
                } else {
                    setFilePreviews(prev => [...prev, null]);
                }
            });
            setTimeout(() => textareaRef.current?.focus(), 0);
        }
    };

    const handleSendFile = async () => {
        if (selectedFiles.length === 0 || !onSendFile) return;
        const filesToSend = [...selectedFiles];
        const captionToSend = message.trim();
        clearFile();
        setMessage('');
        setIsUploading(true);
        try {
            await onSendFile(filesToSend[0], captionToSend);
            for (let i = 1; i < filesToSend.length; i++) {
                await onSendFile(filesToSend[i], '');
            }
        } catch (error) {
            console.error('Error enviando archivos:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const clearFile = () => {
        setSelectedFiles([]);
        setFilePreviews([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getFileIcon = (type) => {
        if (type.startsWith('image/')) return <Image className="w-5 h-5" />;
        if (type.startsWith('video/')) return <Film className="w-5 h-5" />;
        if (type.startsWith('audio/')) return <Mic className="w-5 h-5" />;
        return <FileText className="w-5 h-5" />;
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // If recording or has recorded audio, show recording UI
    if (isRecording || audioBlob) {
        return (
            <div style={{ width: '100%', overflow: 'hidden' }}>
                <div className="chat-input-container" style={{
                    backgroundColor: isRecording ? 'rgba(239, 68, 68, 0.1)' : 'var(--color-gray-50)',
                    overflow: 'hidden'
                }}>
                    {/* Cancel button */}
                    <button
                        className="btn btn-icon"
                        onClick={cancelRecording}
                        title="Cancelar"
                        style={{ color: 'var(--color-error)', flexShrink: 0 }}
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>

                    {/* Recording visualization */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: '0 var(--space-2)',
                        minWidth: 0,          // ← critical: allows flex child to shrink below intrinsic size
                        overflow: 'hidden'
                    }}>
                        {isRecording ? (
                            <>
                                {/* Recording indicator */}
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--color-error)',
                                    animation: 'pulse 1s infinite',
                                    flexShrink: 0
                                }} />

                                {/* Waveform */}
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '2px',
                                    height: '40px',
                                    minWidth: 0,
                                    overflow: 'hidden'
                                }}>
                                    {audioWaveform.map((value, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                width: '3px',
                                                height: `${Math.max(4, value * 36)}px`,
                                                backgroundColor: 'var(--color-primary)',
                                                borderRadius: '2px',
                                                transition: 'height 0.1s ease',
                                                flexShrink: 0
                                            }}
                                        />
                                    ))}
                                    {audioWaveform.length === 0 && Array(20).fill(0).map((_, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                width: '3px',
                                                height: '4px',
                                                backgroundColor: 'var(--color-gray-300)',
                                                borderRadius: '2px',
                                                flexShrink: 0
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Timer */}
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-error)',
                                    minWidth: '40px',
                                    flexShrink: 0,
                                    textAlign: 'right'
                                }}>
                                    {formatTime(recordingTime)}
                                </span>
                            </>
                        ) : (
                            <>
                                {/* Audio preview */}
                                <Mic className="w-5 h-5" style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                                <audio
                                    src={audioUrl}
                                    controls
                                    style={{
                                        flex: 1,
                                        height: '36px',
                                        minWidth: 0,   // ← allows audio player to shrink on small screens
                                        width: '100%'
                                    }}
                                />
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-gray-500)',
                                    flexShrink: 0
                                }}>
                                    {formatTime(recordingTime)}
                                </span>
                            </>
                        )}
                    </div>

                    {/* Stop/Send button */}
                    {isRecording ? (
                        <button
                            className="btn btn-send"
                            onClick={stopRecording}
                            title="Detener grabación"
                            style={{ backgroundColor: 'var(--color-error)', flexShrink: 0 }}
                        >
                            <Square className="w-5 h-5" fill="white" />
                        </button>
                    ) : (
                        <button
                            className="btn btn-send"
                            onClick={sendAudio}
                            disabled={isUploading}
                            title="Enviar audio"
                            style={{ backgroundColor: 'var(--color-primary-light)', flexShrink: 0 }}
                        >
                            {isUploading ? '⏳' : <Send className="w-5 h-5" />}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            {/* Quick Reply Suggestions Popup - Moved here for better positioning context */}
            {showQuickReplies && (
                <div className="quick-reply-popup" style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '0',
                    width: '100%',
                    maxHeight: '300px',
                    backgroundColor: 'white',
                    borderTopLeftRadius: 'var(--radius-lg)',
                    borderTopRightRadius: 'var(--radius-lg)',
                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    overflowY: 'auto',
                    border: '1px solid var(--color-gray-200)',
                    marginBottom: '0'
                }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid var(--color-gray-100)', fontSize: '12px', fontWeight: 600, color: 'var(--color-gray-500)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>RESPUESTAS RÁPIDAS</span>
                        <button onClick={() => setShowQuickReplies(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X className="w-3 h-3" /></button>
                    </div>

                    {filteredReplies.length > 0 ? (
                        filteredReplies.map(reply => (
                            <div
                                key={reply.id}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--color-gray-50)',
                                    transition: 'background-color 0.2s',
                                    cursor: 'default' // Default cursor for container
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                {/* Main clickable area for selecting reply */}
                                <div
                                    onClick={() => handleQuickReplySelect(reply)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flex: 1,
                                        cursor: 'pointer',
                                        minWidth: 0 // For text overflow
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: 'var(--color-primary-light)',
                                        borderRadius: '50%',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        flexShrink: 0
                                    }}>
                                        /
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ fontWeight: 600, color: 'var(--color-gray-900)' }}>{reply.shortcut}</div>
                                        <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {reply.content}
                                        </div>
                                    </div>
                                    {reply.media_url && <Image className="w-4 h-4 text-gray-400" />}
                                </div>

                                {/* Edit Button */}
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingQuickReply(reply);
                                            setShowQuickReplies(false);
                                            setShowQuickReplyManager(true);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '6px',
                                            cursor: 'pointer',
                                            color: 'var(--color-gray-400)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                        }}
                                        title="Editar"
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-gray-200)';
                                            e.currentTarget.style.color = 'var(--color-primary)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'var(--color-gray-400)';
                                        }}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm('¿Eliminar esta respuesta rápida?')) {
                                                deleteQuickReply(reply.id);
                                            }
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '6px',
                                            cursor: 'pointer',
                                            color: 'var(--color-gray-400)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                        }}
                                        title="Eliminar"
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = '#FEE2E2';
                                            e.currentTarget.style.color = '#EF4444';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'var(--color-gray-400)';
                                        }}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-gray-500)', fontSize: '13px' }}>
                            No hay coincidencias para "{quickReplyFilter}"
                        </div>
                    )}

                    {/* Create new option */}
                    <button
                        onClick={() => {
                            setShowQuickReplies(false);
                            setShowQuickReplyManager(true);
                        }}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'var(--color-gray-50)',
                            color: 'var(--color-primary)',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: 600,
                            borderTop: '1px solid var(--color-gray-200)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: '2px dashed var(--color-primary)',
                            color: 'var(--color-primary)'
                        }}>
                            <Plus className="w-4 h-4" />
                        </div>
                        Crear nueva respuesta rápida...
                    </button>
                </div>
            )}

            {/* Reply Preview */}
            {replyToMessage && (
                <div style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-gray-100)',
                    borderBottom: '1px solid var(--color-gray-200)',
                    borderLeft: '4px solid var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'relative'
                }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: 'var(--color-primary)',
                            marginBottom: '2px'
                        }}>
                            {replyToMessage.sender_name || replyToMessage.agent_name || replyToMessage.agentName || (replyToMessage.sender === 'agent' ? 'Tú' : 'Cliente')}
                        </p>
                        <p style={{
                            fontSize: '13px',
                            color: 'var(--color-gray-600)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            margin: 0
                        }}>
                            {replyToMessage.text || (replyToMessage.media_type ? `[${replyToMessage.media_type}]` : 'Archivo')}
                        </p>
                    </div>
                    <button
                        onClick={onCancelReply}
                        className="btn btn-icon"
                        style={{ padding: '4px', color: 'var(--color-gray-400)' }}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Editing Preview */}
            {editingMessage && (
                <div style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-blue-50)',
                    borderBottom: '1px solid var(--color-blue-200)',
                    borderLeft: '4px solid var(--color-blue-500)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'relative'
                }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: 'var(--color-blue-600)',
                            marginBottom: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <Edit2 className="w-3 h-3" /> Editando mensaje
                        </p>
                        <p style={{
                            fontSize: '13px',
                            color: 'var(--color-gray-600)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            margin: 0
                        }}>
                            {editingMessage.text}
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            onCancelEdit();
                            setMessage(''); // Clear box on cancel edit
                        }}
                        className="btn btn-icon"
                        style={{ padding: '4px', color: 'var(--color-gray-400)' }}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* File preview */}
            {selectedFiles.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-4)',
                    backgroundColor: 'var(--color-gray-100)',
                    borderBottom: '1px solid var(--color-gray-200)',
                    maxHeight: '150px',
                    overflowY: 'auto'
                }}>
                    {selectedFiles.map((file, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            {filePreviews[index] ? (
                                <img
                                    src={filePreviews[index]}
                                    alt="Preview"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover',
                                        borderRadius: 'var(--radius-md)'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'var(--color-gray-200)',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--color-gray-600)'
                                }}>
                                    {getFileIcon(file.type)}
                                </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    margin: 0
                                }}>
                                    {file.name}
                                </p>
                                <p style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-gray-500)',
                                    margin: 0
                                }}>
                                    {formatFileSize(file.size)}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    const newFiles = [...selectedFiles];
                                    const newPreviews = [...filePreviews];
                                    newFiles.splice(index, 1);
                                    newPreviews.splice(index, 1);
                                    setSelectedFiles(newFiles);
                                    setFilePreviews(newPreviews);
                                    if (newFiles.length === 0 && fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                    }
                                }}
                                className="btn btn-icon"
                                style={{ color: 'var(--color-error)' }}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Tenant Indicator */}
            {currentTenant && (
                <div style={{
                    padding: '4px 16px',
                    backgroundColor: 'var(--color-gray-50)',
                    borderTop: '1px solid var(--color-gray-200)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <MapPin size={12} color="var(--color-primary)" />
                    <span style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        color: 'var(--color-gray-600)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        Respondiendo por: <span style={{ color: 'var(--color-primary)' }}>{currentTenant.name}</span>
                    </span>
                </div>
            )}

            {/* Input area */}
            <div className="chat-input-container" style={{ borderTop: 'none', paddingTop: '4px' }}>
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                {/* Attach button */}
                <button
                    className="btn btn-icon"
                    title="Adjuntar archivo"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || isUploading}
                    style={{
                        flexShrink: 0,
                        backgroundColor: selectedFiles.length > 0 ? 'var(--color-primary-light)' : 'transparent',
                        color: selectedFiles.length > 0 ? 'var(--color-white)' : 'var(--color-gray-600)'
                    }}
                >
                    <Paperclip className="w-5 h-5" />
                </button>

                {/* Quick Reply Manager Modal */}
                {showQuickReplyManager && (
                    <QuickReplyManager
                        isOpen={showQuickReplyManager}
                        onClose={() => {
                            setShowQuickReplyManager(false);
                            setEditingQuickReply(null); // Clear editing state
                            fetchQuickReplies(); // Refresh list after closing
                        }}
                        initialContent={!editingQuickReply ? message.replace('/', '') : ''} // Setup initial content only if not editing
                        initialData={editingQuickReply}
                    />
                )}

                {/* Emoji button */}
                <div style={{ position: 'relative' }}>
                    <button
                        ref={emojiButtonRef}
                        className="btn btn-icon"
                        title="Emojis"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        disabled={disabled || isUploading}
                        style={{
                            flexShrink: 0,
                            color: showEmojiPicker ? 'var(--color-primary)' : 'var(--color-gray-600)'
                        }}
                    >
                        <Smile className="w-5 h-5" />
                    </button>

                    {showEmojiPicker && (
                        <div
                            ref={emojiPickerRef}
                            style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: 0,
                                marginBottom: '10px',
                                zIndex: 1000,
                                boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                background: 'white',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Picker Header with Close Button */}
                            <div style={{
                                padding: '8px 12px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                borderBottom: '1px solid var(--color-gray-100)',
                                background: 'var(--color-gray-50)'
                            }}>
                                <button
                                    onClick={() => setShowEmojiPicker(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--color-gray-400)',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.color = '#EF4444'}
                                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-gray-400)'}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                autoFocusSearch={false}
                                theme="light"
                                width={320}
                                height={400}
                                searchPlaceholder="Buscar emoji..."
                            />
                        </div>
                    )}
                </div>

                {/* Templates Button (Only for official tenants) */}
                {isOfficialTenant && (
                    <>
                        <button
                            className="btn btn-icon"
                            title="Plantillas Oficiales"
                            onClick={() => setShowTemplateModal(true)}
                            disabled={disabled || isUploading}
                            style={{
                                flexShrink: 0,
                                color: 'var(--color-gray-600)'
                            }}
                        >
                            <LayoutTemplate className="w-5 h-5" />
                        </button>
                        <WaTemplateSelectorModal
                            isOpen={showTemplateModal}
                            onClose={() => setShowTemplateModal(false)}
                            currentPhone={currentPhone}
                        />
                    </>
                )}

                <textarea
                    ref={textareaRef}
                    className="message-input"
                    placeholder={selectedFiles.length > 0 ? "Añade un mensaje (opcional)..." : "Escribe un mensaje..."}
                    value={message}
                    onChange={(e) => {
                        setMessage(e.target.value);
                        if (showEmojiPicker) setShowEmojiPicker(false);
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 250) + 'px';
                    }}
                    onPaste={(e) => {
                        const items = e.clipboardData?.items;
                        if (items) {
                            for (let i = 0; i < items.length; i++) {
                                if (items[i].type.indexOf('image') !== -1) {
                                    const file = items[i].getAsFile();
                                    if (file) {
                                        handleFileSelect({ target: { files: [file] } });
                                        e.preventDefault();
                                        break;
                                    }
                                }
                            }
                        }
                    }}
                    onKeyDown={(e) => {
                        // Escape closes the quick reply panel
                        if (e.key === 'Escape' && showQuickReplies) {
                            e.preventDefault();
                            setShowQuickReplies(false);
                            return;
                        }

                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();

                            // If a file is already loaded (e.g. quick reply with media was selected),
                            // always send — don't let the QR panel interfere.
                            if (selectedFiles.length > 0) {
                                handleSubmit();
                                return;
                            }

                            if (showQuickReplies && filteredReplies.length >= 1) {
                                // Select the first (top) quick reply on Enter
                                handleQuickReplySelect(filteredReplies[0]);
                            } else {
                                handleSubmit();
                            }
                        }
                    }}
                    disabled={disabled || isUploading}
                    style={{
                        padding: '12px 16px',
                        fontSize: isMobile ? '16px' : '14px',
                        resize: 'none',
                        minHeight: '50px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        lineHeight: '1.5'
                    }}
                />

                {/* Mic button - hidden when typing */}
                {!message.trim() && selectedFiles.length === 0 && (
                    <button
                        className="btn btn-icon"
                        onClick={startRecording}
                        disabled={disabled || isUploading}
                        title="Grabar audio"
                        style={{
                            flexShrink: 0,
                            color: 'var(--color-gray-600)'
                        }}
                    >
                        <Mic className="w-5 h-5" />
                    </button>
                )}

                {/* Send button - always visible */}
                <button
                    className="btn btn-send"
                    onClick={handleSubmit}
                    disabled={(!message.trim() && selectedFiles.length === 0) || disabled || isUploading}
                    title={selectedFiles.length > 0 ? "Enviar archivo(s)" : "Enviar mensaje"}
                    style={{
                        flexShrink: 0,
                        backgroundColor: (message.trim() || selectedFiles.length > 0)
                            ? 'var(--color-primary)'
                            : 'var(--color-gray-300)'
                    }}
                >
                    {isUploading ? '⏳' : <Send className="w-5 h-5" />}
                </button>
            </div>

            {/* Clear input button (canequita) - centered below text area */}
            {(message.trim() || selectedFiles.length > 0) && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '6px',
                    paddingBottom: '2px'
                }}>
                    <button
                        type="button"
                        onClick={() => {
                            setMessage('');
                            clearFile();
                            if (textareaRef.current) {
                                textareaRef.current.style.height = 'auto';
                                textareaRef.current.focus();
                            }
                        }}
                        title="Limpiar mensaje e imágenes"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 12px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fecaca';
                            e.currentTarget.style.borderColor = '#f87171';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fee2e2';
                            e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                    >
                        <Trash2 style={{ width: '14px', height: '14px' }} />
                        <span>Limpiar todo</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default MessageInput;
